import React from 'react';

/**
 * Page-local inline glyphs the shared `Icon` set doesn't carry (download / cloud
 * upload). Drawn with `stroke="currentColor"` so the caller tints them via `color`
 * (no raw colour literal). Kept here rather than added to the shared `Icon` because
 * they're specific to the Casino Settings logo controls.
 */
export type GlyphProps = {
  size?: number;
  className?: string;
};

export const DownloadGlyph = ({ size = 16, className }: GlyphProps): React.ReactElement => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 4v10" />
    <path d="M8 10.5l4 4 4-4" />
    <path d="M5 19h14" />
  </svg>
);

export const UploadGlyph = ({ size = 16, className }: GlyphProps): React.ReactElement => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 16V5.5" />
    <path d="M8 9.5l4-4 4 4" />
    <path d="M5 19h14" />
  </svg>
);

/**
 * Generic "image" placeholder mark (frame + sun + hill) shown inside each ghost tile
 * while the casino's real logos are still being generated — it reads clearly as
 * "artwork will go here", never as a finished logo. Drawn with `stroke="currentColor"`
 * so the caller tints it via `color` (no raw colour literal).
 */
export const ImagePlaceholderGlyph = ({ size = 22, className }: GlyphProps): React.ReactElement => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
    <circle cx="8.5" cy="10" r="1.6" />
    <path d="M21 16.5l-5-4.5-9 7.5" />
  </svg>
);

export type LogoMarkProps = {
  size?: number;
  /** Fill — pass a theme token; defaults to `currentColor`. */
  color?: string;
  className?: string;
};

/**
 * The casino brand mark used as the Website Logos section icon. Rendered monochrome
 * (all paths inherit one `fill`) so it reads cleanly at icon size on the dark panel;
 * the colour comes from the caller (a token), never a literal.
 */
export const LogoMarkGlyph = ({ size = 22, color = 'currentColor', className }: LogoMarkProps): React.ReactElement => (
  <svg className={className} width={size} height={size} viewBox="0 0 64 64" fill={color}>
    <path d="M10.54 57.62V56.3c1.829-.206 3.2-.894 4.114-2.063.915-1.169 2.482-4.37 4.702-9.601L34.17 9.74h1.415l17.716 40.312c1.182 2.67 2.124 4.321 2.83 4.954.707.633 1.893 1.064 3.559 1.294v1.32H41.608V56.3c2.077-.184 3.417-.408 4.019-.673.601-.265.902-.916.902-1.952 0-.347-.116-.955-.348-1.831s-.556-1.797-.972-2.766l-2.848-6.84H23.624c-1.847 4.653-2.95 7.492-3.309 8.516-.359 1.026-.539 1.838-.539 2.437 0 1.198.486 2.027 1.458 2.488.6.276 1.732.484 3.396.621v1.32zm30.623-18.264-8.27-18.989-8.066 18.989z" />
    <path d="m32.904 2.227 26.05 59.357-.484.213L32.42 2.439zM24.639 2.222l26.05 59.358-.484.212-26.05-59.357z" />
    <path d="m36.923 2.107.484.207L11.941 61.93l-.485-.207zM40.442 2.108l.485.207L15.46 61.93l-.485-.207z" />
    <path d="M8.473 56.867C3.801 56.867 0 53.065 0 48.395c0-4.672 3.801-8.474 8.473-8.474s8.473 3.802 8.473 8.474c0 4.67-3.8 8.472-8.473 8.472zm0-16.418c-4.381 0-7.945 3.563-7.945 7.945 0 4.381 3.564 7.944 7.945 7.944s7.945-3.563 7.945-7.944-3.564-7.945-7.945-7.945zM59.221 55.987a4.784 4.784 0 0 1-4.778-4.778 4.784 4.784 0 0 1 4.778-4.779c2.636 0 4.779 2.144 4.779 4.779s-2.144 4.778-4.779 4.778zm0-9.03c-2.344 0-4.251 1.908-4.251 4.252s1.907 4.251 4.251 4.251c2.345 0 4.252-1.907 4.252-4.251s-1.908-4.252-4.252-4.252zM44.709 55.768a1.746 1.746 0 0 1 0-3.489c.962 0 1.745.782 1.745 1.745 0 .961-.783 1.744-1.745 1.744zm0-2.962a1.219 1.219 0 0 0 0 2.435 1.218 1.218 0 0 0 0-2.435zM22.941 56.119a3.023 3.023 0 0 1-3.02-3.021c0-1.665 1.354-3.02 3.02-3.02s3.02 1.354 3.02 3.02a3.023 3.023 0 0 1-3.02 3.021zm0-5.512a2.494 2.494 0 0 0-2.492 2.491 2.495 2.495 0 0 0 2.492 2.493 2.495 2.495 0 0 0 2.492-2.493 2.495 2.495 0 0 0-2.492-2.491zM2.023 57.746h60.775v.528H2.023z" />
    <path d="M2.023 42.091h60.775v.528H2.023zM2.023 39.013h60.775v.527H2.023z" />
  </svg>
);

export default DownloadGlyph;
